<template>
  <div :class="$style.container">
    <div :class="$style.image">
      <NsImage :src="imageUrl" maintain-ratio="4x3" :image-fit="imageFit" />
      <div v-if="isVideo" :class="$style.videoBtn" @click="handleOpenLightbox">
        <NsIcon
          :class="$style.videoIcon"
          size="custom"
          icon-name="icon-play-outline"
        />
      </div>
    </div>
    <div v-if="title" :class="$style.title" v-html="title" />
    <div v-if="contentText" :class="$style.contentText" v-html="contentText" />
    <div v-if="linkUrl && linkText" :class="$style.link">
      <a v-if="phabletAndDown" class="primary-ghost fluid" :href="linkUrl">
        {{ linkText }}
      </a>
      <NsExpressiveLink v-else :href="linkUrl">{{ linkText }}</NsExpressiveLink>
    </div>
    <NsLightbox
      v-if="isVideo"
      :open="lightboxOpen"
      :video-src="videoSrc"
      :video-src-tags="videoSrcTags"
      @close="handleCloseLightbox"
    />
  </div>
</template>

<script>
import { NsExpressiveLink } from "@nuskin/design-components";
import { NsIcon } from "@nuskin/ns-icon";
import NsLightbox from "./NsLightbox.vue";
import NsImage from "./NsImage.vue";
export default {
  name: "NsInformationCard",
  components: { NsExpressiveLink, NsLightbox, NsImage, NsIcon },
  props: {
    /**
     * The content text
     */
    contentText: {
      type: String,
      default: ""
    },
    /**
     * The image fit
     * @values cover, contain
     */
    imageFit: {
      type: String,
      default: "cover",
      validator: value => ["cover", "contain"].includes(value)
    },
    /**
     * The image url
     */
    imageUrl: {
      type: String,
      default: ""
    },
    /**
     * The link's label
     */
    linkText: {
      type: String,
      default: ""
    },
    /**
     * The url the link will take you to
     */
    linkUrl: {
      type: String,
      default: ""
    },
    /**
     * The title label
     */
    title: {
      type: String,
      default: ""
    },
    /**
     * The video source url
     */
    videoSrc: {
      type: String,
      default: ""
    },
    /**
     * Source tags can be used instead of ctaUrl
     */
    videoSrcTags: {
      type: String,
      default: ""
    }
  },
  data: () => ({
    lightboxOpen: false
  }),
  computed: {
    phabletAndDown() {
      return this.$mq === "phablet" || this.$mq === "mobile";
    },
    isVideo() {
      const vidExt = ["mov", "avi", "wmv", "flv", "3gp", "mp4", "mpg", "webm"];
      return vidExt.some(ext => this.videoSrc.endsWith(`.${ext}`));
    }
  },
  methods: {
    /**
     * Closes lightbox
     */
    handleCloseLightbox() {
      this.lightboxOpen = false;
    },
    handleOpenLightbox() {
      this.lightboxOpen = true;
    }
  }
};
</script>

<style lang="scss" module>
@use "sass:map";
@use "~@nuskin/ns-core-styles/src/scss/colors";
@use "~@nuskin/ns-core-styles/src/scss/other";
@use '~@nuskin/ns-core-styles/src/scss/global.mixins';
@use '~@nuskin/ns-core-styles/src/scss/typographyVariables';
@use "~@nuskin/ns-core-styles/src/scss/globalVariables";

.container,
.container * {
  box-sizing: border-box;
  font-family: map.get(typographyVariables.$typography, font-family-primary);
}

.container {
  padding: 15px 5px;
  max-width: 350px;
  width: 100%;
  background: #ffffff;
}

.image {
  position: relative;
}

.videoBtn {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  color: rgba(255, 255, 255, 0.8);
  cursor: pointer;

  &:hover {
    color: #ffffff;
  }

  .videoIcon {
    width: 80px;
    height: 80px;
  }
}

.title {
  @include global.title-bold-italic-caps();
  margin: 15px 0;
  color: map.get(colors.$themeColors, color-primary-text);
  letter-spacing: 1.5px;
}

.contentText {
  @include global.p-large();
  margin: 15px 0;
  color: map.get(colors.$themeColors, color-primary-text);
}

.link {
  margin: 20px 0 5px;
  color: map.get(colors.$themeColors, color-primary-text);

  a {
    color: map.get(colors.$themeColors, color-primary-text);
  }
}

@media (min-width: 768px) {
  .container {
    padding: 15px;
  }

  .contentText {
    @include global.p-medium();
  }
}
</style>
